/**
 * Predykat STRUKTURY numeru telefonu — wspólny dla backendu i frontu.
 *
 * VP-243: makra reklamowe Meta trafiały do pola telefonu po przejściu przez
 * form-urlencode (spacje → '+'), np. nazwa kampanii "... AUTOWEBINAR 5 | 18.12.2025"
 * zapisywała się jako "++++5++18122025+". Po obcięciu plusów zostaje "518122025" —
 * poprawny polski numer komórkowy, którego żadna walidacja formatu już nie odróżni,
 * a po którym identity-stitching scalał setki różnych osób w jedną tożsamość.
 * Dlatego test MUSI działać na wartości surowej, zanim plusy znikną.
 *
 * Celowo bez `libphonenumber-js` — pełna walidacja E.164 należy do backendu
 * (`sanitizePhone`), a frontowi wystarcza strażnik wyświetlania i nie warto mu
 * dokładać ~145 kB do bundla. Ten moduł jest jedynym źródłem prawdy o strukturze;
 * odpowiednik w SQL siedzi w tooling/scripts/migrate-data-vp-243-*.ts i przy zmianie
 * reguły trzeba go zaktualizować ręcznie.
 */

/** Zakres cyfr dopuszczalny dla numeru (E.164 dopuszcza maksymalnie 15). */
export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 15;

/**
 * Usuwa niewidzialne znaki formatujące Unicode (LTR/RTL marks U+202A–U+202E,
 * zero-width, BOM). iOS i WhatsApp owijają nimi kopiowane numery: "‪+48452832422‬" —
 * '+' lądował wtedy na pozycji 1 i poprawny numer wyglądał jak "plus w środku".
 */
export function stripInvisible(raw: string): string {
  return raw.replace(/\p{Cf}/gu, "");
}

/**
 * Czy wartość ma strukturę numeru: zero liter, '+' wyłącznie jako pierwszy znak
 * (prefiks E.164). Nie sprawdza, czy numer istnieje — od tego jest walidator.
 */
export function hasPhoneShape(raw: string): boolean {
  if (/[A-Za-z]/.test(raw)) return false;
  return !/(?!^)\+/.test(raw);
}

/**
 * Struktura + wiarygodna liczba cyfr. Wspólny wstęp dla `sanitizePhone` (backend,
 * dokłada walidację libphonenumber) i `isDisplayablePhone` (front, na tym poprzestaje).
 * Zwraca wartość po usunięciu niewidzialnych znaków albo `null`.
 */
export function phoneShapeOrNull(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const cleaned = stripInvisible(raw).trim();
  if (!cleaned || !hasPhoneShape(cleaned)) return null;
  const digits = cleaned.replace(/\D/g, "");
  if (digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) return null;
  return cleaned;
}
